import { useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  App as AntApp,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';

import {
  useCreateOrderMutation,
  useOrdersQuery,
  type Order,
  type OrdersQueryParams,
  type OrderStatus,
} from './hooks';
import { useCustomersQuery, type Customer } from '@/customers/hooks';
import { ErrorAlert } from '@/components/ErrorAlert';
import { ApiError } from '@starling/core/api/errors';
import { formatDateTime } from '@/lib/format';
import { OrderStatusTag } from '@/orders/OrderStatusTag';
import { BlockedActionTooltip } from '@/components/BlockedActionTooltip';
import { useTenantBlocked } from '@/settings/hooks';

const { Title, Text } = Typography;

type StatusFilter = OrderStatus | 'all';

const STATUS_VALUES: StatusFilter[] = [
  'all',
  'accepted',
  'in_progress',
  'ready',
  'issued',
  'cancelled',
];

function parseStatusParam(raw: string | null): StatusFilter {
  if (raw && (STATUS_VALUES as string[]).includes(raw)) {
    return raw as StatusFilter;
  }
  return 'all';
}

interface CreateValues {
  customer_id: string;
  number: string;
  note?: string;
}

export function OrdersPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm<CreateValues>();

  const page = Number(searchParams.get('page') ?? '1') || 1;
  const pageSize = Number(searchParams.get('page_size') ?? '20') || 20;
  const status = parseStatusParam(searchParams.get('status'));
  const customerId = searchParams.get('customer_id') ?? undefined;

  const queryParams: OrdersQueryParams = {
    page,
    pageSize,
    ...(status !== 'all' ? { status } : {}),
    ...(customerId ? { customerId } : {}),
  };
  const { data, isLoading, error } = useOrdersQuery(queryParams);
  const { isBlocked } = useTenantBlocked();

  // Подгружаем сразу 100 клиентов на старте — для мапы id→customer в строках
  // таблицы и для Select в Create-модалке. Это допустимая срезка для MVP
  // (см. ТЗ M2.6 §1: «100-элементный cap для MVP»). При росте базы стоит
  // ввести батчевое получение по id (фаза 2).
  const customersQuery = useCustomersQuery({ page: 1, pageSize: 100, linked: 'all' });
  const customersById = new Map<string, Customer>(
    (customersQuery.data?.items ?? []).map((c) => [c.id, c]),
  );

  const createMutation = useCreateOrderMutation();

  const setStatus = (next: StatusFilter): void => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'all') sp.delete('status');
    else sp.set('status', next);
    sp.set('page', '1');
    setSearchParams(sp);
  };

  const onCreateSubmit = (values: CreateValues): void => {
    createMutation.mutate(
      {
        customer_id: values.customer_id,
        number: values.number.trim(),
        ...(values.note ? { note: values.note } : {}),
      },
      {
        onSuccess: (created) => {
          form.resetFields();
          setCreateOpen(false);
          void message.success(t('orders.create_modal.success'));
          navigate(`/orders/${created.id}`);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.code === 'order_number_already_exists') {
              void message.error(t('orders.errors.number_already_exists'));
            } else if (err.code === 'customer_not_found') {
              void message.error(t('orders.errors.customer_not_found'));
            } else {
              void message.error(err.message);
            }
          } else {
            void message.error(t('errors.generic'));
          }
        },
      },
    );
  };

  const columns: ColumnsType<Order> = [
    {
      title: t('orders.table.number'),
      dataIndex: 'number',
      key: 'number',
      width: 140,
      render: (n: string) => <span className="sl-mono">{n}</span>,
    },
    {
      title: t('orders.table.customer'),
      key: 'customer',
      render: (_v, o) => {
        const c = customersById.get(o.customer_id);
        if (!c) return <Text type="secondary">—</Text>;
        return (
          <Space size={8}>
            <Text strong>{c.name}</Text>
            {c.linked_at ? (
              <Tag color="cyan">{t('customers.linked_badge')}</Tag>
            ) : (
              <Tag>{t('customers.unlinked_badge')}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: t('orders.table.note'),
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (n: string | null) =>
        n ? <Text type="secondary">{n}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: t('orders.table.status'),
      key: 'status',
      width: 150,
      render: (_v, o) => <OrderStatusTag status={o.status} />,
    },
    {
      title: t('orders.table.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (v: string) => (
        <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
          {formatDateTime(v)}
        </Text>
      ),
    },
  ];

  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  // Счётчики табов: значение «текущего среза» (с учётом серверной пагинации
  // не очень точно — серверный total мы знаем только для выбранного статуса).
  // На M2.6 показываем `total` рядом с активным таб'ом, остальные — без числа.
  // Это честнее, чем считать по items на странице (вводит в заблуждение).
  const tabItems = STATUS_VALUES.map((s) => ({
    key: s,
    label: (
      <Space size={6}>
        <span>{t(`orders.filters.${s}`)}</span>
        {s === status && <Tag style={{ marginInlineEnd: 0 }}>{total}</Tag>}
      </Space>
    ),
  }));

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('orders.title')}
          </Title>
          <Text type="secondary">{t('orders.subtitle')}</Text>
        </div>
        <BlockedActionTooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            disabled={isBlocked}
            onClick={() => setCreateOpen(true)}
          >
            {t('orders.new_button')}
          </Button>
        </BlockedActionTooltip>
      </div>

      <Card style={{ marginBottom: 16 }} variant="outlined">
        <Tabs activeKey={status} onChange={(k) => setStatus(k as StatusFilter)} items={tabItems} />
      </Card>

      {error && <ErrorAlert error={error} />}

      <Table<Order>
        rowKey="id"
        loading={isLoading}
        dataSource={items}
        columns={columns}
        onRow={(record) => ({
          onClick: () => navigate(`/orders/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (p, ps) => {
            const sp = new URLSearchParams(searchParams);
            sp.set('page', String(p));
            sp.set('page_size', String(ps));
            setSearchParams(sp);
          },
        }}
        locale={{
          emptyText: (
            <div style={{ padding: '32px 0', textAlign: 'center' }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {t('orders.empty.title')}
              </Text>
              <Text type="secondary">{t('orders.empty.subtitle')}</Text>
            </div>
          ),
        }}
      />

      <Modal
        title={t('orders.create_modal.title')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending}
        okText={t('orders.create_modal.submit')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form<CreateValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onCreateSubmit}
        >
          <Form.Item
            label={t('orders.create_modal.customer')}
            name="customer_id"
            rules={[{ required: true, message: t('orders.create_modal.customer_required') }]}
          >
            <Select
              placeholder={t('orders.create_modal.customer_placeholder')}
              showSearch
              loading={customersQuery.isLoading}
              optionFilterProp="label"
              options={(customersQuery.data?.items ?? []).map((c) => ({
                value: c.id,
                label: `${c.name} · ${c.phone}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            label={t('orders.create_modal.number')}
            name="number"
            rules={[
              { required: true, message: t('orders.create_modal.number_required') },
              { max: 64 },
            ]}
          >
            <Input className="sl-mono" placeholder="2026-0125" />
          </Form.Item>
          <Form.Item label={t('orders.create_modal.note')} name="note">
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
        </Form>
        {createMutation.error && (
          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
            <ErrorAlert error={createMutation.error} />
          </Space>
        )}
      </Modal>
    </div>
  );
}
